// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  admin/src/pages/AdminWallet.jsx  —  Quickry
//  Copy to: C:\quickry\admin\src\pages\AdminWallet.jsx
//  Route add karo: <Route path="/wallet" element={<AdminWallet/>}/>
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import {
  Wallet, CheckCircle2, X, RefreshCw, Loader2,
  AlertCircle, User, Phone, Mail, Clock,
  TrendingUp, IndianRupee, List, Filter,
  ChevronDown, Search, Check, Ban,
} from "lucide-react";

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";

const getToken = () => localStorage.getItem("adminToken") || localStorage.getItem("token") || "";

/* ── Design tokens — match your admin panel ── */
const C = {
  bg:        "#f2f3f6",
  white:     "#ffffff",
  text:      "#1c1c1e",
  sub:       "#6b7280",
  muted:     "#9ca3af",
  border:    "#e5e7eb",
  primary:   "#2563eb",
  light:     "#eff6ff",
  green:     "#16a34a",
  greenLight:"#dcfce7",
  red:       "#ef4444",
  redLight:  "#fef2f2",
  yellow:    "#d97706",
  yellowLight:"#fef3c7",
  purple:    "#7c3aed",
};

/* ── Confirm Modal ── */
function ConfirmModal({ title, message, onConfirm, onClose, loading, type="approve" }) {
  return (
    <div onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:C.white, borderRadius:20, width:"100%", maxWidth:360, padding:"28px 24px", boxShadow:"0 20px 60px rgba(0,0,0,0.18)", fontFamily:"system-ui,sans-serif" }}>
        <div style={{ width:56, height:56, borderRadius:"50%", background:type==="approve"?C.greenLight:C.redLight, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
          {type === "approve"
            ? <CheckCircle2 size={26} color={C.green}/>
            : <Ban size={26} color={C.red}/>}
        </div>
        <div style={{ fontWeight:800, fontSize:"1.1rem", textAlign:"center", marginBottom:8, color:C.text }}>{title}</div>
        <div style={{ fontSize:"0.83rem", color:C.sub, textAlign:"center", lineHeight:1.65, marginBottom:22 }}>{message}</div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onClose}
            style={{ flex:1, background:C.bg, border:`1.5px solid ${C.border}`, borderRadius:12, padding:"11px", fontWeight:700, fontSize:"0.85rem", cursor:"pointer", color:C.text, fontFamily:"inherit" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ flex:2, background:type==="approve"?`linear-gradient(135deg,#166534,${C.green})`:`linear-gradient(135deg,#dc2626,${C.red})`, border:"none", borderRadius:12, padding:"11px", fontWeight:800, fontSize:"0.85rem", cursor:loading?"not-allowed":"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7, opacity:loading?0.75:1 }}>
            {loading ? <><Loader2 size={15} style={{ animation:"spin .75s linear infinite" }}/> Wait...</> : type==="approve" ? <><Check size={15}/> Approve</> : <><Ban size={14}/> Reject</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Reject Reason Modal ── */
function RejectModal({ onConfirm, onClose, loading }) {
  const [reason, setReason] = useState("");
  return (
    <div onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:C.white, borderRadius:20, width:"100%", maxWidth:380, padding:"24px", fontFamily:"system-ui,sans-serif" }}>
        <div style={{ fontWeight:800, fontSize:"1rem", marginBottom:6, color:C.text }}>Reject Request</div>
        <div style={{ fontSize:"0.8rem", color:C.sub, marginBottom:14 }}>Rejection reason batao (optional)</div>
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. UTR invalid, duplicate request..."
          style={{ width:"100%", border:`1.5px solid ${C.border}`, borderRadius:10, padding:"11px 14px", fontSize:"0.88rem", color:C.text, background:C.bg, fontFamily:"inherit", outline:"none", marginBottom:14 }}/>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onClose}
            style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px", fontWeight:700, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit" }}>
            Cancel
          </button>
          <button onClick={() => onConfirm(reason)} disabled={loading}
            style={{ flex:2, background:`linear-gradient(135deg,#dc2626,${C.red})`, border:"none", borderRadius:10, padding:"10px", fontWeight:800, fontSize:"0.82rem", cursor:loading?"not-allowed":"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
            {loading ? <Loader2 size={15} style={{ animation:"spin .75s linear infinite" }}/> : <Ban size={14}/>} Reject
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════ */
export default function AdminWallet() {
  const [activeTab,   setActiveTab]   = useState("pending");
  const [pending,     setPending]     = useState([]);
  const [allTxns,     setAllTxns]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [allLoading,  setAllLoading]  = useState(false);
  const [stats,       setStats]       = useState({ totalPending:0, totalApproved:0, totalAmount:0 });
  const [toast,       setToast]       = useState(null);

  // Confirm modals
  const [approveItem, setApproveItem] = useState(null);
  const [rejectItem,  setRejectItem]  = useState(null);
  const [actionLoad,  setActionLoad]  = useState(false);

  // Filters for all transactions
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType,   setFilterType]   = useState("");
  const [search,       setSearch]       = useState("");

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Fetch pending requests ── */
  const fetchPending = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/wallet/admin/pending`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setPending(data.requests || []);
        // Stats
        const total  = (data.requests||[]).reduce((s, r) => s + r.amount, 0);
        setStats(s => ({ ...s, totalPending: data.requests?.length || 0, totalAmount: total }));
      }
    } catch {}
    setLoading(false);
  };

  /* ── Fetch all transactions ── */
  const fetchAll = async () => {
    setAllLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterType)   params.set("type",   filterType);
      const res  = await fetch(`${API}/wallet/admin/all?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setAllTxns(data.transactions || []);
        const approved = (data.transactions||[]).filter(t => t.status==="approved" && t.type==="credit");
        setStats(s => ({ ...s, totalApproved: approved.length }));
      }
    } catch {}
    setAllLoading(false);
  };

  useEffect(() => { fetchPending(); }, []);
  useEffect(() => { if (activeTab === "all") fetchAll(); }, [activeTab, filterStatus, filterType]);

  /* ── Approve ── */
  const handleApprove = async () => {
    if (!approveItem) return;
    setActionLoad(true);
    try {
      const res  = await fetch(`${API}/wallet/admin/approve/${approveItem._id}`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setPending(p => p.filter(x => x._id !== approveItem._id));
        showToast(`✅ ₹${approveItem.amount} approved! User: ${approveItem.user?.name || approveItem.user?.email}`);
        setApproveItem(null);
      } else showToast(data.message || "Approve failed", "error");
    } catch { showToast("Server error", "error"); }
    setActionLoad(false);
  };

  /* ── Reject ── */
  const handleReject = async (reason) => {
    if (!rejectItem) return;
    setActionLoad(true);
    try {
      const res  = await fetch(`${API}/wallet/admin/reject/${rejectItem._id}`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization: `Bearer ${getToken()}` },
        body:    JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (data.success) {
        setPending(p => p.filter(x => x._id !== rejectItem._id));
        showToast(`Request reject kar di — ₹${rejectItem.amount}`, "info");
        setRejectItem(null);
      } else showToast(data.message || "Reject failed", "error");
    } catch { showToast("Server error", "error"); }
    setActionLoad(false);
  };

  /* ── Filtered search ── */
  const filteredAll = allTxns.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.user?.name?.toLowerCase().includes(q) ||
      t.user?.email?.toLowerCase().includes(q) ||
      t.user?.phone?.includes(q) ||
      t.utr?.toLowerCase().includes(q)
    );
  });

  const toastBg = toast?.type === "error" ? C.red : toast?.type === "info" ? C.primary : C.green;

  return (
    <div style={{ background:C.bg, minHeight:"100vh", fontFamily:"system-ui,sans-serif", color:C.text }}>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        button,input{font-family:inherit;outline:none}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:100px}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
        .slide-down{animation:slideDown .3s ease both}
      `}</style>

      {/* Toast */}
      {toast && (
        <div className="slide-down" style={{ position:"fixed", top:20, right:20, background:toastBg, color:"#fff", padding:"12px 20px", borderRadius:12, fontSize:"0.85rem", fontWeight:600, zIndex:900, boxShadow:"0 8px 24px rgba(0,0,0,0.18)", maxWidth:340 }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"18px 24px", boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:14, background:C.light, border:`1px solid ${C.primary}30`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Wallet size={22} color={C.primary}/>
            </div>
            <div>
              <div style={{ fontWeight:800, fontSize:"1.2rem", color:C.text }}>Wallet Management</div>
              <div style={{ fontSize:"0.72rem", color:C.sub }}>Customer deposit requests approve / reject karo</div>
            </div>
          </div>
          <button onClick={() => { fetchPending(); if (activeTab === "all") fetchAll(); }}
            style={{ display:"flex", alignItems:"center", gap:6, background:C.light, border:`1px solid ${C.primary}30`, borderRadius:10, padding:"9px 16px", color:C.primary, fontWeight:700, fontSize:"0.82rem", cursor:"pointer" }}>
            <RefreshCw size={14} style={{ animation:(loading||allLoading)?"spin .75s linear infinite":"none" }}/> Refresh
          </button>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"20px 24px" }}>

        {/* Stats Cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:12, marginBottom:20 }}>
          {[
            { label:"Pending Requests", value:stats.totalPending, icon:Clock,       color:C.yellow,  bg:C.yellowLight },
            { label:"Total Pending ₹",  value:`₹${stats.totalAmount}`, icon:IndianRupee, color:C.primary, bg:C.light   },
            { label:"Approved Today",   value:stats.totalApproved, icon:CheckCircle2, color:C.green,   bg:C.greenLight },
          ].map((s, i) => (
            <div key={i} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:16, padding:"16px 18px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <div style={{ fontSize:"0.7rem", fontWeight:700, color:C.sub, textTransform:"uppercase", letterSpacing:"0.8px" }}>{s.label}</div>
                <div style={{ width:34, height:34, borderRadius:10, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <s.icon size={16} color={s.color}/>
                </div>
              </div>
              <div style={{ fontWeight:900, fontSize:"1.5rem", color:C.text }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", background:C.white, borderRadius:14, padding:4, border:`1px solid ${C.border}`, marginBottom:16, width:"fit-content" }}>
          {[{ id:"pending", label:`Pending (${pending.length})` }, { id:"all", label:"All Transactions" }].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ padding:"9px 20px", borderRadius:10, border:"none", background:activeTab===t.id?C.primary:"transparent", color:activeTab===t.id?"#fff":C.sub, fontWeight:activeTab===t.id?700:400, fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ━━ PENDING TAB ━━ */}
        {activeTab === "pending" && (
          <div>
            {loading ? (
              <div style={{ textAlign:"center", padding:60 }}>
                <Loader2 size={32} color={C.primary} style={{ animation:"spin .75s linear infinite" }}/>
              </div>
            ) : pending.length === 0 ? (
              <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:18, padding:"60px 20px", textAlign:"center" }}>
                <CheckCircle2 size={52} color={C.border} strokeWidth={1} style={{ margin:"0 auto 16px", display:"block" }}/>
                <div style={{ fontWeight:700, color:C.sub, fontSize:"0.95rem" }}>Koi pending requests nahi</div>
                <div style={{ fontSize:"0.78rem", color:C.muted, marginTop:5 }}>Sab requests process ho gayi hain</div>
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:12 }}>
                {pending.map(req => (
                  <div key={req._id} style={{ background:C.white, border:`1.5px solid ${C.yellowLight}`, borderRadius:18, padding:"18px", boxShadow:"0 2px 12px rgba(217,119,6,0.08)" }}>
                    {/* User info */}
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:42, height:42, borderRadius:12, background:C.light, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <User size={19} color={C.primary}/>
                        </div>
                        <div>
                          <div style={{ fontWeight:700, fontSize:"0.9rem", color:C.text }}>{req.user?.name || "—"}</div>
                          <div style={{ fontSize:"0.72rem", color:C.sub }}>{req.user?.email || req.user?.phone || "—"}</div>
                        </div>
                      </div>
                      <div style={{ background:C.yellowLight, color:C.yellow, fontSize:"0.65rem", fontWeight:800, padding:"4px 10px", borderRadius:100, border:`1px solid ${C.yellow}30` }}>
                        PENDING
                      </div>
                    </div>

                    {/* Details */}
                    <div style={{ background:C.bg, borderRadius:12, padding:"12px 14px", marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                        <span style={{ fontSize:"0.72rem", color:C.sub }}>Amount</span>
                        <span style={{ fontWeight:900, fontSize:"1.1rem", color:C.green }}>₹{req.amount}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                        <span style={{ fontSize:"0.72rem", color:C.sub }}>UTR / Txn ID</span>
                        <span style={{ fontWeight:700, fontSize:"0.8rem", color:C.text, letterSpacing:"0.5px" }}>{req.utr || "—"}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                        <span style={{ fontSize:"0.72rem", color:C.sub }}>Current Wallet</span>
                        <span style={{ fontWeight:700, fontSize:"0.8rem", color:C.primary }}>₹{req.user?.walletBalance || 0}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between" }}>
                        <span style={{ fontSize:"0.72rem", color:C.sub }}>Requested At</span>
                        <span style={{ fontSize:"0.75rem", color:C.muted }}>{new Date(req.createdAt).toLocaleString("en-IN")}</span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => setRejectItem(req)}
                        style={{ flex:1, background:C.redLight, border:`1px solid #fecaca`, borderRadius:10, padding:"10px", color:C.red, fontWeight:700, fontSize:"0.8rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>
                        <Ban size={14}/> Reject
                      </button>
                      <button onClick={() => setApproveItem(req)}
                        style={{ flex:2, background:`linear-gradient(135deg,#166534,${C.green})`, border:"none", borderRadius:10, padding:"10px", color:"#fff", fontWeight:800, fontSize:"0.85rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, boxShadow:"0 4px 14px rgba(22,163,74,0.25)" }}>
                        <CheckCircle2 size={15}/> Approve ₹{req.amount}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ━━ ALL TRANSACTIONS TAB ━━ */}
        {activeTab === "all" && (
          <div>
            {/* Filters */}
            <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
              <div style={{ position:"relative", flex:"1 1 180px" }}>
                <Search size={14} style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:C.muted }}/>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name, email, UTR..."
                  style={{ width:"100%", border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 12px 10px 34px", fontSize:"0.83rem", color:C.text, background:C.white }}/>
              </div>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 12px", fontSize:"0.83rem", color:C.text, background:C.white, cursor:"pointer" }}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 12px", fontSize:"0.83rem", color:C.text, background:C.white, cursor:"pointer" }}>
                <option value="">All Types</option>
                <option value="credit">Credit (Top-up)</option>
                <option value="debit">Debit (Order)</option>
              </select>
            </div>

            {allLoading ? (
              <div style={{ textAlign:"center", padding:60 }}>
                <Loader2 size={32} color={C.primary} style={{ animation:"spin .75s linear infinite" }}/>
              </div>
            ) : filteredAll.length === 0 ? (
              <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:18, padding:"60px 20px", textAlign:"center" }}>
                <List size={44} color={C.border} strokeWidth={1} style={{ margin:"0 auto 14px", display:"block" }}/>
                <div style={{ fontWeight:700, color:C.sub }}>Koi transactions nahi</div>
              </div>
            ) : (
              <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:18, overflow:"hidden" }}>
                {/* Table header */}
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1.2fr 1.2fr 1fr 1fr 1fr", gap:0, padding:"12px 18px", borderBottom:`1px solid ${C.border}`, background:C.bg }}>
                  {["User","Amount","UTR","Type","Status","Time"].map(h => (
                    <div key={h} style={{ fontSize:"0.65rem", fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.8px" }}>{h}</div>
                  ))}
                </div>
                {filteredAll.map((t, i) => {
                  const isCredit  = t.type === "credit";
                  const sc = t.status === "approved" ? C.green : t.status === "pending" ? C.yellow : C.red;
                  const scBg = t.status === "approved" ? C.greenLight : t.status === "pending" ? C.yellowLight : C.redLight;
                  return (
                    <div key={t._id||i} style={{ display:"grid", gridTemplateColumns:"2fr 1.2fr 1.2fr 1fr 1fr 1fr", gap:0, padding:"13px 18px", borderBottom:i<filteredAll.length-1?`1px solid ${C.border}`:"none", alignItems:"center" }}
                      onMouseEnter={e => e.currentTarget.style.background=C.bg}
                      onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:"0.83rem", color:C.text }}>{t.user?.name || "—"}</div>
                        <div style={{ fontSize:"0.68rem", color:C.muted }}>{t.user?.email || t.user?.phone || "—"}</div>
                      </div>
                      <div style={{ fontWeight:800, fontSize:"0.88rem", color:isCredit?C.green:C.red }}>
                        {isCredit?"+":"-"}₹{t.amount}
                      </div>
                      <div style={{ fontSize:"0.75rem", color:C.sub, fontFamily:"monospace" }}>{t.utr || "—"}</div>
                      <div>
                        <span style={{ background:isCredit?"#eff6ff":"#fef2f2", color:isCredit?C.primary:C.red, fontSize:"0.62rem", fontWeight:700, padding:"3px 8px", borderRadius:100 }}>
                          {isCredit?"CREDIT":"DEBIT"}
                        </span>
                      </div>
                      <div>
                        <span style={{ background:scBg, color:sc, fontSize:"0.62rem", fontWeight:700, padding:"3px 8px", borderRadius:100 }}>
                          {t.status?.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize:"0.7rem", color:C.muted }}>{new Date(t.createdAt).toLocaleDateString("en-IN")}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Approve Confirm Modal */}
      {approveItem && (
        <ConfirmModal
          type="approve"
          title={`Approve ₹${approveItem.amount}?`}
          message={`${approveItem.user?.name || "User"} ka wallet balance ₹${(approveItem.user?.walletBalance||0) + approveItem.amount} ho jayega.\nUTR: ${approveItem.utr}`}
          onConfirm={handleApprove}
          onClose={() => setApproveItem(null)}
          loading={actionLoad}
        />
      )}

      {/* Reject Modal */}
      {rejectItem && (
        <RejectModal
          onConfirm={handleReject}
          onClose={() => setRejectItem(null)}
          loading={actionLoad}
        />
      )}
    </div>
  );
}